import { useState } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  cn,
} from '@pantry/ui';
import { logger } from '../../lib/logger';
import {
  useUpdateItemInstance,
  CONSUME_REASONS,
  CONSUME_REASON_LABELS,
  WASTE_REASONS,
  WASTE_REASON_LABELS,
  type ConsumeReason,
  type WasteReason,
  type ItemInstance,
  type ItemUnit,
} from '@pantry/api-client';
import { UNIT_DISPLAY_LABELS } from '@pantry/shared';
import {
  CheckIcon,
  TrashIcon,
  FireIcon,
  UsersIcon,
  GiftIcon,
  SparklesIcon,
  BeakerIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  QuestionMarkCircleIcon,
  ArchiveBoxXMarkIcon,
} from '@heroicons/react/24/outline';

interface RemoveItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  householdId: string;
  itemId: string;
  itemName: string;
  unit: ItemUnit;
  activeInstances: ItemInstance[];
  onSuccess: () => void;
}

type ActionType = 'consume' | 'waste' | null;

/**
 * Get icon for consume reason
 */
function getConsumeReasonIcon(reason: ConsumeReason) {
  switch (reason) {
    case 'cooked':
      return FireIcon;
    case 'eaten_raw':
      return SparklesIcon;
    case 'shared':
      return UsersIcon;
    case 'donated':
      return GiftIcon;
    case 'other':
    default:
      return BeakerIcon;
  }
}

/**
 * Get icon for waste reason
 */
function getWasteReasonIcon(reason: WasteReason) {
  switch (reason) {
    case 'expired':
      return ClockIcon;
    case 'spoiled':
      return ExclamationTriangleIcon;
    case 'damaged':
      return NoSymbolIcon;
    case 'other':
    default:
      return QuestionMarkCircleIcon;
  }
}

/**
 * Dialog for removing an item by consuming or wasting all active stock.
 * Replaces the hard delete functionality to maintain tracking statistics.
 */
export function RemoveItemDialog({
  open,
  onOpenChange,
  householdId,
  itemId,
  itemName,
  unit,
  activeInstances,
  onSuccess,
}: RemoveItemDialogProps) {
  const { t } = useLingui();
  const [actionType, setActionType] = useState<ActionType>(null);
  const [consumeReason, setConsumeReason] = useState<ConsumeReason | null>(null);
  const [wasteReason, setWasteReason] = useState<WasteReason | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const updateInstance = useUpdateItemInstance();

  const totalQuantity = activeInstances.reduce((sum, i) => sum + i.quantity, 0);
  const batchCount = activeInstances.length;

  const handleClose = () => {
    setActionType(null);
    setConsumeReason(null);
    setWasteReason(null);
    setStatusNotes('');
    setError(null);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    setError(null);

    if (actionType === 'consume' && !consumeReason) {
      setError(t`Please select how you used this item`);
      return;
    }

    if (actionType === 'waste' && !wasteReason) {
      setError(t`Please select a reason for wasting this item`);
      return;
    }

    setIsProcessing(true);

    try {
      // Process all active instances
      for (const instance of activeInstances) {
        await updateInstance.mutateAsync({
          householdId,
          itemId,
          instanceId: instance.id,
          data: {
            status: actionType === 'consume' ? 'consumed' : 'wasted',
            ...(actionType === 'consume'
              ? { consumeReason: consumeReason! }
              : { wasteReason: wasteReason! }),
            statusNotes: statusNotes.trim() || undefined,
          },
        });
      }

      handleClose();
      onSuccess();
    } catch (err) {
      logger.error('Failed to remove item', err, {
        householdId,
        itemId,
        instanceCount: activeInstances.length,
      });
      setError(err instanceof Error ? err.message : t`Failed to update item`);
      setIsProcessing(false);
    }
  };

  const canSubmit =
    actionType &&
    ((actionType === 'consume' && consumeReason) || (actionType === 'waste' && wasteReason));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
              <ArchiveBoxXMarkIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <Trans>Remove Item</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Remove <span className="font-medium text-foreground">{itemName}</span> from your
              inventory by marking all stock as used or wasted. This helps track your consumption
              patterns.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          {/* Summary */}
          <div className="rounded-xl bg-muted/30 border border-border/50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                <Trans>Total to remove:</Trans>
              </span>
              <span className="font-medium text-foreground">
                {totalQuantity} {UNIT_DISPLAY_LABELS[unit]}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">
                <Trans>Stock batches:</Trans>
              </span>
              <span className="font-medium text-foreground">
                <Plural value={batchCount} one="# batch" other="# batches" />
              </span>
            </div>
          </div>

          {/* Action selection */}
          {!actionType && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                <Trans>What happened to this item?</Trans>
              </Label>
              <div className="grid gap-3">
                <button
                  type="button"
                  onClick={() => setActionType('consume')}
                  className="flex items-center gap-4 w-full p-4 rounded-xl border border-border/50 bg-card hover:bg-fresh/5 hover:border-fresh/30 transition-all duration-200 text-left"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-fresh/10">
                    <CheckIcon className="h-6 w-6 text-fresh" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      <Trans>I used it</Trans>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Trans>Mark as consumed (cooked, eaten, shared, etc.)</Trans>
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setActionType('waste')}
                  className="flex items-center gap-4 w-full p-4 rounded-xl border border-border/50 bg-card hover:bg-expired/5 hover:border-expired/30 transition-all duration-200 text-left"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-expired/10">
                    <TrashIcon className="h-6 w-6 text-expired" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      <Trans>I had to throw it away</Trans>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Trans>Mark as wasted (expired, spoiled, damaged, etc.)</Trans>
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Consume reason selection */}
          {actionType === 'consume' && (
            <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-200">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActionType(null)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Trans>← Back</Trans>
                </button>
                <Label className="text-sm font-medium">
                  <Trans>How did you use it?</Trans>
                </Label>
              </div>
              <div className="grid gap-2">
                {CONSUME_REASONS.map((reason, index) => {
                  const Icon = getConsumeReasonIcon(reason);
                  const isSelected = consumeReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setConsumeReason(reason)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 animate-in fade-in',
                        isSelected
                          ? 'border-fresh bg-fresh/5 shadow-sm'
                          : 'border-border/50 bg-card hover:bg-muted/50 hover:border-border'
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg mr-3 transition-colors',
                          isSelected ? 'bg-fresh/20' : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 transition-colors',
                            isSelected ? 'text-fresh' : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-medium transition-colors',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {CONSUME_REASON_LABELS[reason]}
                      </span>
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-fresh flex items-center justify-center animate-in zoom-in duration-200">
                          <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Waste reason selection */}
          {actionType === 'waste' && (
            <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-200">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActionType(null)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Trans>← Back</Trans>
                </button>
                <Label className="text-sm font-medium">
                  <Trans>What happened?</Trans>
                </Label>
              </div>
              <div className="grid gap-2">
                {WASTE_REASONS.map((reason, index) => {
                  const Icon = getWasteReasonIcon(reason);
                  const isSelected = wasteReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setWasteReason(reason)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 animate-in fade-in',
                        isSelected
                          ? 'border-expired bg-expired/5 shadow-sm'
                          : 'border-border/50 bg-card hover:bg-muted/50 hover:border-border'
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg mr-3 transition-colors',
                          isSelected ? 'bg-expired/20' : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 transition-colors',
                            isSelected ? 'text-expired' : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-medium transition-colors',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {WASTE_REASON_LABELS[reason]}
                      </span>
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-expired flex items-center justify-center animate-in zoom-in duration-200">
                          <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes (only show when action type is selected) */}
          {actionType && (
            <div className="space-y-2 animate-in fade-in duration-200">
              <Label htmlFor="statusNotes" className="text-sm font-medium">
                <Trans>Notes</Trans>{' '}
                <span className="text-muted-foreground font-normal">
                  <Trans>(optional)</Trans>
                </span>
              </Label>
              <textarea
                id="statusNotes"
                className={cn(
                  'flex min-h-[80px] w-full rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-sm',
                  'placeholder:text-muted-foreground focus-visible:outline-none transition-all resize-none',
                  actionType === 'consume' &&
                    'focus-visible:ring-2 focus-visible:ring-fresh/50 focus-visible:border-fresh/50',
                  actionType === 'waste' &&
                    'focus-visible:ring-2 focus-visible:ring-expired/50 focus-visible:border-expired/50'
                )}
                placeholder={t`Any additional details...`}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                maxLength={500}
              />
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            <Trans>Cancel</Trans>
          </Button>
          {actionType && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isProcessing}
              className={cn(
                'min-w-[140px]',
                actionType === 'consume' && 'bg-fresh hover:bg-fresh/90',
                actionType === 'waste' && 'bg-expired hover:bg-expired/90'
              )}
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <Trans>Processing...</Trans>
                </div>
              ) : actionType === 'consume' ? (
                <Trans>Mark All as Used</Trans>
              ) : (
                <Trans>Mark All as Wasted</Trans>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
